/**
 * CompanyActivityTab — unified Timeline + Change History view for a customer.
 *
 * Top:    Vertical timeline of significant events (newest first), color-coded per type.
 * Bottom: Searchable / filterable detail table with full audit rows + JSON before/after.
 *
 * Data source: GET /api/companies/:id/activity which UNIONs Companies.CreatedAt
 * with rows from AuditEvents scoped to the company.
 */
import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import api from '../api/client';
import {
  RefreshCw, Sparkles, UserPlus, Building2, Clock, Zap, Tag,
  ToggleRight, ToggleLeft, Shield, FileEdit, AlertTriangle, Filter,
  Search, ChevronDown, ChevronRight
} from 'lucide-react';

// ----- Event type metadata: icon, label, color -----
const EVENT_META = {
  COMPANY_CREATED:  { icon: Building2,    label: 'Customer added',     color: 'emerald' },
  UPDATE_MODULES:   { icon: ToggleRight,  label: 'Modules updated',    color: 'blue'    },
  START_TRIAL:      { icon: Clock,        label: 'Trial started',      color: 'purple'  },
  END_TRIAL:        { icon: ToggleLeft,   label: 'Trial ended',        color: 'gray'    },
  CONVERT_TRIAL:    { icon: Zap,          label: 'Trial converted',    color: 'emerald' },
  APPLY_DISCOUNT:   { icon: Tag,          label: 'Discount applied',   color: 'amber'   },
  REMOVE_DISCOUNT:  { icon: Tag,          label: 'Discount removed',   color: 'gray'    },
  CREATE_USER:      { icon: UserPlus,     label: 'User added',         color: 'blue'    },
  UPDATE_USER:      { icon: FileEdit,     label: 'User updated',       color: 'gray'    },
  DELETE_USER:      { icon: AlertTriangle,label: 'User deleted',       color: 'red'     },
  UPDATE_PERMISSIONS:{ icon: Shield,      label: 'Permissions changed',color: 'indigo'  },
  UPDATE_SSO:       { icon: Shield,       label: 'SSO settings changed', color: 'indigo' },
  DEFAULT:          { icon: Sparkles,     label: 'Activity',           color: 'gray'    },
};

const COLOR_CLASSES = {
  emerald: { dot: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  blue:    { dot: 'bg-blue-500',    bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200' },
  purple:  { dot: 'bg-purple-500',  bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200' },
  amber:   { dot: 'bg-amber-500',   bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200' },
  red:     { dot: 'bg-red-500',     bg: 'bg-red-50',     text: 'text-red-700',     border: 'border-red-200' },
  indigo:  { dot: 'bg-indigo-500',  bg: 'bg-indigo-50',  text: 'text-indigo-700',  border: 'border-indigo-200' },
  gray:    { dot: 'bg-gray-400',    bg: 'bg-gray-50',    text: 'text-gray-700',    border: 'border-gray-200' },
};

function getMeta(action) {
  return EVENT_META[action] || { ...EVENT_META.DEFAULT, label: action };
}

function formatRelative(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMo = Math.round(diffDay / 30);
  if (diffMo < 12) return `${diffMo}mo ago`;
  return `${Math.round(diffMo / 12)}y ago`;
}

function formatAbsolute(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString();
}

export default function CompanyActivityTab({ targetCompanyId, companyName }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/companies/${targetCompanyId}/activity?limit=500`);
      setEvents(res.data?.events || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [targetCompanyId]);

  useEffect(() => { load(); }, [load]);

  const uniqueActions = useMemo(() => {
    const set = new Set(events.map(e => e.action));
    return [...set].sort();
  }, [events]);

  const filteredEvents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter(e => {
      if (actionFilter && e.action !== actionFilter) return false;
      if (!q) return true;
      const haystack = [
        e.action, e.summary, e.actor, e.entityType,
        JSON.stringify(e.before || ''), JSON.stringify(e.after || ''),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [events, search, actionFilter]);

  // Show top 12 in the timeline (most recent significant events)
  const TIMELINE_ACTIONS = new Set([
    'COMPANY_CREATED', 'START_TRIAL', 'END_TRIAL', 'CONVERT_TRIAL',
    'UPDATE_MODULES', 'APPLY_DISCOUNT', 'REMOVE_DISCOUNT', 'UPDATE_SSO',
  ]);
  const timelineEvents = useMemo(() => {
    return filteredEvents.filter(e => TIMELINE_ACTIONS.has(e.action)).slice(0, 12);
  }, [filteredEvents]);

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-3 text-gray-500">
        <RefreshCw className="w-5 h-5 animate-spin" /> Loading activity…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8 text-red-600">
        <p className="font-semibold mb-2">Failed to load activity</p>
        <p className="text-sm">{error}</p>
        <button onClick={load} className="mt-4 btn btn-secondary">Retry</button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            Activity &amp; Change History
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Every meaningful change to <span className="font-medium">{companyName}</span> —
            modules, trials, discounts, SSO and user actions — recorded for audit.
          </p>
        </div>
        <button onClick={load} className="btn btn-secondary text-sm inline-flex items-center gap-1">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* ============ TIMELINE ============ */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
          Timeline
        </h4>
        {timelineEvents.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500">
            No timeline events to show yet.
          </div>
        ) : (
          <ol className="relative border-l-2 border-gray-200 ml-4 space-y-4">
            {timelineEvents.map(ev => {
              const meta = getMeta(ev.action);
              const Icon = meta.icon;
              const c = COLOR_CLASSES[meta.color] || COLOR_CLASSES.gray;
              return (
                <li key={ev.id} className="ml-6">
                  <span className={`absolute -left-[11px] flex items-center justify-center w-5 h-5 rounded-full ring-4 ring-white ${c.dot}`}>
                    <Icon className="w-3 h-3 text-white" />
                  </span>
                  <div className={`p-3 rounded-lg border ${c.border} ${c.bg}`}>
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <span className={`text-sm font-semibold ${c.text}`}>{meta.label}</span>
                      <span className="text-xs text-gray-500" title={formatAbsolute(ev.at)}>
                        {formatRelative(ev.at)}
                      </span>
                    </div>
                    {ev.summary && (
                      <p className="text-sm text-gray-700 mt-1">{ev.summary}</p>
                    )}
                    {ev.actor && (
                      <p className="text-xs text-gray-500 mt-1">by {ev.actor}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* ============ FULL HISTORY TABLE ============ */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
          Change history
        </h4>

        <div className="flex flex-wrap gap-2 mb-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search action, summary, actor, before/after JSON…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All actions</option>
              {uniqueActions.map(a => (
                <option key={a} value={a}>{getMeta(a).label} ({a})</option>
              ))}
            </select>
          </div>
          <span className="self-center text-xs text-gray-500">
            {filteredEvents.length} event{filteredEvents.length === 1 ? '' : 's'}
          </span>
        </div>

        {filteredEvents.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500">
            No events match your filters.
          </div>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-8 px-2 py-2"></th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase text-xs">When</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase text-xs">Action</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase text-xs">Summary</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase text-xs">Actor</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEvents.map(ev => {
                  const meta = getMeta(ev.action);
                  const Icon = meta.icon;
                  const c = COLOR_CLASSES[meta.color] || COLOR_CLASSES.gray;
                  const isExpanded = expandedRow === ev.id;
                  const hasDetail = !!(ev.before || ev.after);
                  return (
                    <Fragment key={ev.id}>
                      <tr className={`hover:bg-gray-50 ${ev.isSecurityCritical ? 'bg-red-50/50' : ''}`}>
                        <td className="px-2 py-2">
                          {hasDetail && (
                            <button
                              onClick={() => setExpandedRow(isExpanded ? null : ev.id)}
                              className="text-gray-400 hover:text-gray-700"
                            >
                              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600" title={formatAbsolute(ev.at)}>
                          {formatRelative(ev.at)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.text}`}>
                            <Icon className="w-3 h-3" /> {meta.label}
                          </span>
                          {ev.isSecurityCritical && (
                            <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 rounded uppercase">
                              security
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-800">
                          {ev.summary || <span className="text-gray-400 italic">—</span>}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {ev.actor || <span className="text-gray-400 italic">system</span>}
                        </td>
                      </tr>
                      {isExpanded && hasDetail && (
                        <tr key={`${ev.id}-detail`} className="bg-gray-50">
                          <td></td>
                          <td colSpan={4} className="px-3 py-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                              <div>
                                <div className="font-semibold text-gray-700 mb-1">Before</div>
                                <pre className="p-2 bg-white border border-gray-200 rounded overflow-x-auto text-gray-700">
{ev.before ? JSON.stringify(ev.before, null, 2) : '(none)'}
                                </pre>
                              </div>
                              <div>
                                <div className="font-semibold text-gray-700 mb-1">After</div>
                                <pre className="p-2 bg-white border border-gray-200 rounded overflow-x-auto text-gray-700">
{ev.after ? JSON.stringify(ev.after, null, 2) : '(none)'}
                                </pre>
                              </div>
                            </div>
                            {ev.ipAddress && (
                              <div className="text-[11px] text-gray-500 mt-2">IP: {ev.ipAddress}</div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
